<template>
  <span>
    <input
      ref="input"
      v-model="value"
      :name="uniqueInputName"
      :class="`variant-${variant} { error: errorMessage, 'cursor-not-allowed opacity-50': disabled } w-full`"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      v-bind="$attrs"
      v-on="validationListeners"
    />

    <div class="collapsed-overlay" :class="{ 'collapsed-overlay-active': !errorMessage }" :style="animationStyle">
      <small id="text-error" class="p-error">{{ errorMessage || "&nbsp;" }}</small>
    </div>
  </span>
</template>

<script setup lang="ts">
import { getCurrentInstance, ref, watch } from "vue";
import { useField } from "vee-validate";
import { validateRules } from "@/utils/rules/rules";
import { onMounted } from "vue";

type BtnVariant = "primary" | "secondary";

const props = withDefaults(
  defineProps<{
    id: string;
    modelValue: string;
    variant?: BtnVariant;
    placeholder?: string;
    type?: string;
    disabled?: boolean;
    focusOnInit?: boolean;
    rules?: RuleFunction<string>[];
  }>(),
  {
    type: "text",
    disabled: false,
    focusOnInit: false,
    rules: () => [],
    variant: "primary",
  }
);

const instance = getCurrentInstance();
let uniqueInputName = ref("input");
if (instance) {
  uniqueInputName.value = instance.uid.toString();
}

const input = ref<HTMLInputElement>();

onMounted(() => {
  if (props.focusOnInit) input.value?.focus();
});

const { value, errorMessage, handleChange, handleBlur } = useField(uniqueInputName.value, validateField, {
  syncVModel: true,
  validateOnValueUpdate: false,
});

const validationListeners = {
  blur: (e: Event | undefined) => handleBlur(e, true),
  input: (e: Event | undefined) => handleChange(e, !!errorMessage.value),
};

async function validateField(value: string) {
  return await validateRules(props.rules, value);
}

const animationDuration = 300;
const animationStyle = ref(`
  transition: opacity ${animationDuration}ms, visibility ${animationDuration}ms, max-height ${animationDuration}ms;
`);
watch(errorMessage, () => {
  setTimeout(() => {
    window.dispatchEvent(new Event("resize"));
  }, animationDuration);
});

const focus = () => {
  input.value?.focus();
};

defineExpose({
  focus,
});

defineOptions({
  inheritAttrs: false,
});
</script>

<style lang="scss" scoped>
.collapsed-overlay {
  opacity: 1;
  visibility: visible;
  max-height: 3rem;
}

.collapsed-overlay-active {
  opacity: 0;
  visibility: hidden;
  max-height: 0;
}
</style>

<style scoped lang="scss">
.variant- {
  &primary {
    border: 1px solid #e4e4e4;
    border-radius: 0.15rem;
    font-size: 0.78rem;
    padding: 0.47rem 0.55rem;
    @apply text-theme-1 outline-none transition-all;

    &::placeholder {
      @apply text-theme-1/60;
    }

    &.error {
      @apply border border-theme-error;
    }

    &:enabled {
      &:focus {
        @apply border-theme-3/50 shadow-input-focused;
      }

      &:hover {
        @apply border-theme-3/50;
      }
    }
  }
  &secondary {
    border: none;
    border-bottom: 1px solid #e4e4e4;
    border-radius: 0;
    font-size: 0.78rem;
    padding: 0.47rem 0.55rem;

    @apply text-theme-1 outline-none transition-all;

    &::placeholder {
      @apply text-theme-1/60;
    }

    &.error {
      @apply border-b border-theme-error;
    }

    &:enabled {
      &:focus {
        @apply border-b border-theme-3/50;
      }

      &:hover {
        @apply border-b border-theme-3/50;
      }
    }
  }
}
</style>
